import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";

const lines = ["CHLOÉ", "RIGALLAUD", "—LEAL"];

// depth : amplitude parallaxe souris (px) ; speed : vitesse parallaxe scroll
const floats = [
  { src: "/images/synrgy/cans-front.webp", label: "Synrgy", cls: "left-[4%] top-[12%] w-28 md:w-48", depth: 30, speed: -0.18, delay: 1.3, target: { kind: "project", num: "07" } },
  { src: "/images/aethera.webp", label: "Aethera", cls: "right-[5%] top-[10%] w-20 md:w-36", depth: 55, speed: -0.32, delay: 1.45, target: { kind: "project", num: "09" } },
  { src: "/images/dossier-137.webp", label: "Dossier 137", cls: "left-[10%] bottom-[9%] w-20 md:w-36", depth: 45, speed: -0.12, delay: 1.6, target: { kind: "project", num: "01" } },
  { src: "/images/fisheye.webp", label: "Fisheye", cls: "right-[12%] bottom-[8%] w-28 md:w-52", depth: 70, speed: -0.26, delay: 1.75, target: { kind: "project", num: "08" } },
  { src: "/images/workshop-mumbai.webp", label: "Workshop Mumbaï", cls: "left-[40%] top-[5%] w-16 md:w-28 hidden md:block", depth: 90, speed: -0.4, delay: 1.9, target: { kind: "project", num: "06" } },
  { src: "/images/beauty-of-joseon/photo-set-design.webp", label: "Beauty of Joseon", cls: "right-[34%] bottom-[4%] w-16 md:w-28 hidden md:block", depth: 80, speed: -0.22, delay: 2.05, target: { kind: "photo", name: "Beauty of Joseon", p: 0 } },
  { src: "/images/photo-arbre-rouge.webp", label: "Série Rouge", cls: "left-[26%] bottom-[22%] w-14 md:w-24 hidden lg:block", depth: 110, speed: -0.5, delay: 2.2, target: { kind: "photo", name: "Série Rouge", p: 0 } },
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({ y: "0%", transition: { duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: 0.3 + i * 0.14 } }),
};

function FloatImage({ img, mx, my, scrollY, onSelect, testId }) {
  const { t } = useLang();
  const x = useTransform(mx, (v) => v * img.depth);
  const y = useTransform([my, scrollY], ([m, s]) => m * img.depth + s * img.speed);
  const rotate = useTransform(mx, (v) => v * img.depth * 0.08);
  return (
    <motion.div className={`absolute z-0 ${img.cls}`} style={{ x, y, rotate, willChange: "transform" }}>
      <motion.div
        role="button"
        tabIndex={0}
        aria-label={`${t("hero.view")} ${img.label}`}
        data-testid={testId}
        onClick={() => onSelect(img)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(img)}
        className="group relative cursor-pointer pointer-events-auto"
        initial={{ opacity: 0, scale: 0.6, filter: "blur(16px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ delay: img.delay, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
      >
        <img src={img.src} alt={img.label} className="w-full h-auto object-cover opacity-55 group-hover:opacity-100 transition-opacity duration-500 ease-out" />
        <span className="absolute inset-0 border border-[#75FB4E] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <span className="absolute -bottom-6 left-0 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#75FB4E] opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          {img.label} →
        </span>
      </motion.div>
    </motion.div>
  );
}

export default function Hero({ onNavigate }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const textY = useTransform(scrollY, [0, 800], [0, -160]);
  const textOpacity = useTransform(scrollY, [0, 600], [1, 0.15]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 40, damping: 16 });
  const smy = useSpring(my, { stiffness: 40, damping: 16 });
  const nameX = useTransform(smx, (v) => v * -14);
  const nameY = useTransform(smy, (v) => v * -10);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const onSelect = (img) => {
    if (img.target.kind === "project") {
      window.dispatchEvent(new CustomEvent("reveal-project", { detail: img.target.num }));
      setTimeout(() => onNavigate(`#projet-${img.target.num}`), 120);
      setTimeout(() => window.dispatchEvent(new CustomEvent("flash-project", { detail: img.target.num })), 1000);
    } else {
      window.dispatchEvent(new CustomEvent("open-photo", { detail: { name: img.target.name, p: img.target.p } }));
    }
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden px-6 md:px-12 pt-24"
    >
      <div data-testid="hero-float-field" className="absolute inset-0 z-0 pointer-events-none">
        {floats.map((img, i) => (
          <FloatImage key={img.src} img={img} mx={smx} my={smy} scrollY={scrollY} onSelect={onSelect} testId={`hero-float-${i}`} />
        ))}
      </div>

      <motion.h1 style={{ y: textY, x: nameX, opacity: textOpacity, willChange: "transform, opacity" }} className="relative z-10 font-display uppercase leading-[0.85] select-none pointer-events-none">
        {lines.map((line, i) => (
          <span key={line} className="block overflow-hidden py-1">
            <motion.span
              className={`block text-[16vw] md:text-[13vw] tracking-tight ${i === 1 ? "text-outline" : "text-[#E5E5E5]"}`}
              style={i === 2 ? { WebkitTextStroke: "0", color: "#75FB4E", y: nameY, willChange: "transform" } : { y: nameY, willChange: "transform" }}
              custom={i}
              variants={lineVariants}
              initial="hidden"
              animate="visible"
              data-testid={`hero-line-${i}`}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </motion.h1>

      <motion.div
        style={{ opacity: textOpacity }}
        className="relative z-10 mt-12 md:mt-16 flex flex-wrap justify-between gap-4 font-mono-tech text-[10px] md:text-xs tracking-[0.25em] text-[#909090] uppercase pointer-events-none"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
      >
        <span data-testid="hero-tag-role">{t("hero.role")}</span>
        <span className="hidden md:inline">{t("hero.based")}</span>
        <span>{t("hero.portfolio")}</span>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono-tech text-[10px] tracking-[0.3em] text-[#909090]"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0.4, 1] }}
        transition={{ delay: 2, duration: 1.5 }}
      >
        {t("hero.scroll")}
      </motion.div>
    </section>
  );
}
